import clsx from 'clsx';
import { extractionTdClass, extractionThClass } from './tableClasses';

/**
 * Shared cell primitives for the dense extraction tables (DR-2358).
 *
 * Every table used to carry its own identical `Th` / `Td` copy and sprinkled
 * `truncate max-w-[…]` by hand, so long free-text columns (Subject, Remarks,
 * Description, Reason, names) escaped truncation in half the tables and pushed
 * the remaining columns off-screen. Import these instead of re-declaring them.
 *
 * Use `TruncatedTd` for any free-text column; it clips to a max width and puts
 * the full value in the native `title` tooltip. `TruncatedContentTd` is the same
 * treatment for cells whose content is composed (badges, struck-through text)
 * and therefore needs an explicit title string.
 */

/** Default clip width for free-text columns; override per call site via `maxWidth`. */
const DEFAULT_MAX_WIDTH = 'max-w-[260px]';

export function Th({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <th className={clsx(extractionThClass, className)} title={title}>
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  title,
  onClick,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLTableCellElement>;
}) {
  return (
    <td className={clsx(extractionTdClass, 'align-top', className)} title={title} onClick={onClick}>
      {children}
    </td>
  );
}

/**
 * Free-text cell: clipped with an ellipsis, full value on hover, em dash when
 * the value is missing or blank.
 */
export function TruncatedTd({
  value,
  maxWidth = DEFAULT_MAX_WIDTH,
  className,
}: {
  value: string | null | undefined;
  /** Tailwind max-width utility, e.g. `max-w-[240px]`. */
  maxWidth?: string;
  className?: string;
}) {
  const text = value?.trim() ? value : undefined;
  return (
    <Td className={clsx('truncate', maxWidth, className)} title={text}>
      {text ?? '—'}
    </Td>
  );
}

/**
 * Same clipping as `TruncatedTd` for composed children (e.g. `CrossedOutCell`),
 * where the hover text can't be derived from a single rendered string.
 */
export function TruncatedContentTd({
  children,
  title,
  maxWidth = DEFAULT_MAX_WIDTH,
  className,
}: {
  children: React.ReactNode;
  title: string | null | undefined;
  maxWidth?: string;
  className?: string;
}) {
  return (
    <Td className={clsx('truncate', maxWidth, className)} title={title ?? undefined}>
      {children}
    </Td>
  );
}
